import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
} from 'react';
// FirebaseUser is used by Firebase SDK, AuthUser is our simplified version from authService

import { authService, AuthUser } from '../services/auth/authService'; // Corrected import
import { getAuthProvider } from '../services/auth/authProviderFactory';
import Logger from '../utils/logger';
import type { UserRole } from '../constants/userRoles';

// Generate unique provider ID for debugging
const logger = Logger.create('AuthContext');

const AUTH_PROVIDER_ID = Math.random().toString(36).substring(2, 15);
logger.debug(`🆔 AuthProvider Instance Created: ${AUTH_PROVIDER_ID}`);

interface AuthContextType {
  currentUser: AuthUser | null;
  loading: boolean;
  error: string | null;
  signUp: typeof authService.signUp;
  // Provider-abstracted (task_111): Firebase uses email/password, Studio
  // ignores args and redirects to its hosted login.
  signIn: (email?: string, password?: string) => Promise<AuthUser | void>;
  signInWithGoogle: typeof authService.signInWithGoogle;
  signOut: () => Promise<void>;
  // Bearer token for API calls; null on the Studio path (cookie carries auth).
  getIdToken: () => Promise<string | null>;
  getIdTokenResult: typeof authService.getIdTokenResult;
  refreshIdToken: typeof authService.refreshIdToken;
  isAuthenticatedUser: boolean;
  isValidUser: boolean;
  systemRole: UserRole | null;
  isAdmin: boolean;
  isDeveloper: boolean;
  isSalesManager: boolean;
  isFinanceApprover: boolean;
  isProductOwner: boolean;
  isFinalApprover: boolean;
}

// Create the context with a default undefined value to catch consumers not wrapped in a provider
const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  logger.debug(
    `🟢 [${AUTH_PROVIDER_ID}] AuthProvider: Component mounted/rendering`
  );

  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Active auth provider (task_108/111). Firebase or Studio per
  // VITE_AUTH_PROVIDER. Resolved once — switching providers requires a rebuild.
  const authProvider = useMemo(() => getAuthProvider(), []);

  // Debug: Log component mount/unmount
  useEffect(() => {
    logger.debug(`🟢 [${AUTH_PROVIDER_ID}] AuthProvider: Mounted`);
    return () => {
      logger.debug(`🔴 [${AUTH_PROVIDER_ID}] AuthProvider: Unmounted`);
    };
  }, []);

  useEffect(() => {
    logger.debug(`🔄 [${AUTH_PROVIDER_ID}] Setting up auth state listener...`);
    setLoading(true);

    const unsubscribe = authProvider.onAuthStateChanged(
      (user: AuthUser | null) => {
        logger.debug(
          '📱 Auth state changed in context:',
          user ? user.email : 'null'
        );
        setCurrentUser(user);
        setLoading(false);
        setError(null);
      }
    );

    return () => {
      logger.debug(`🧹 [${AUTH_PROVIDER_ID}] Cleaning up auth state listener`);
      unsubscribe();
    };
  }, [authProvider]);

  // Computed values
  const isAuthenticatedUser = authService.isAuthenticatedUser(currentUser);
  const validationResult = authService.validateUserAccess(currentUser);
  const isValidUser = validationResult.isValid;
  // Note: systemRole is typed as UserRole but backend may return other roles
  // We cast to UserRole for type safety on known roles
  const systemRole = (currentUser?.systemRole as UserRole) || null;
  const rawSystemRole = currentUser?.systemRole || null; // For legacy role checks
  const isAdmin = systemRole === 'ADMIN';

  // Additional role checks for commonly used roles
  const isDeveloper = systemRole === 'DEVELOPER';
  const isSalesManager = rawSystemRole === 'SALES_MANAGER'; // Legacy role
  const isFinanceApprover = rawSystemRole === 'FINANCE_APPROVER'; // Legacy role
  const isProductOwner = systemRole === 'PRODUCT_OWNER';
  const isFinalApprover = rawSystemRole === 'FINAL_APPROVER'; // Legacy role

  // If validation fails and we have a user, set the error
  useEffect(() => {
    if (currentUser && !isValidUser) {
      setError(validationResult.reason || 'Access validation failed');
    } else if (currentUser && isValidUser) {
      setError(null);
    }
  }, [currentUser, isValidUser, validationResult.reason]);

  const value: AuthContextType = useMemo(
    () => ({
      currentUser,
      loading,
      error,
      signUp: authService.signUp,
      // Provider-abstracted (task_111). Wrapped so class-method `this` stays
      // bound. signUp / signInWithGoogle / getIdTokenResult / refreshIdToken
      // remain Firebase-only (deleted with their UI in task_112).
      signIn: (email?: string, password?: string) =>
        authProvider.signIn(email, password),
      signInWithGoogle: authService.signInWithGoogle,
      signOut: () => authProvider.signOut(),
      getIdToken: () => authProvider.getToken(),
      getIdTokenResult: authService.getIdTokenResult,
      refreshIdToken: authService.refreshIdToken,
      isAuthenticatedUser,
      isValidUser,
      systemRole,
      isAdmin,
      isDeveloper,
      isSalesManager,
      isFinanceApprover,
      isProductOwner,
      isFinalApprover,
    }),
    [
      authProvider,
      currentUser,
      loading,
      error,
      isAuthenticatedUser,
      isValidUser,
      systemRole,
      isAdmin,
      isDeveloper,
      isSalesManager,
      isFinanceApprover,
      isProductOwner,
      isFinalApprover,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { AuthContext };
